import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Command } from 'cmdk';
import { useEffect, useState } from 'react';
import { getDocStore } from '../../store/doc-store.ts';
import type { TaskDisplayMode } from '../tasks/task-workspace-model.ts';

/**
 * Cmd/Ctrl+K command palette built on Radix Dialog + cmdk's Command.
 *
 * We compose Radix Dialog directly (instead of cmdk's Command.Dialog
 * helper) so we can supply a `Dialog.Title` for screen readers without
 * having it visible — silencing the Radix a11y warnings while keeping
 * cmdk's nice keyboard handling for the command list itself.
 *
 * Commands available in M1:
 *   - New task — focuses the quick-add input
 *   - Switch to <project> — one entry per active project
 *   - Reset local data — wipes IDB and reloads
 */
export interface CommandPaletteProps {
  /** Optional override for tests; defaults to managing its own open state. */
  open?: boolean;
  onOpenChange?(open: boolean): void;
  onSwitchProject?(projectId: string): void;
  onSwitchTaskDisplay?(mode: TaskDisplayMode): void;
}

export function CommandPalette({
  open,
  onOpenChange,
  onSwitchProject,
  onSwitchTaskDisplay,
}: CommandPaletteProps): JSX.Element {
  const store = getDocStore();
  const projects = store.useProjects();
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;

  const setOpen = (next: boolean): void => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };

  // Global Cmd/Ctrl+K toggles the palette.
  useEffect(() => {
    const handler = (event: KeyboardEvent): void => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen(!isOpen);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  const focusQuickAdd = (): void => {
    setOpen(false);
    // Defer to next tick so the dialog tear-down doesn't steal focus back.
    setTimeout(() => {
      const input = document.getElementById('quick-add-input');
      if (input instanceof HTMLInputElement) input.focus();
    }, 0);
  };

  const resetLocal = async (): Promise<void> => {
    setOpen(false);
    if (!window.confirm('Wipe all local data? This cannot be undone.')) return;
    await store.resetLocalData();
    window.location.reload();
  };

  const switchProject = (id: string): void => {
    setOpen(false);
    if (onSwitchProject) onSwitchProject(id);
    else store.setActiveProject(id);
  };

  const newProject = (): void => {
    setOpen(false);
    const name = window.prompt('New project name');
    if (name) store.createProject(name);
  };

  const switchTaskDisplay = (mode: TaskDisplayMode): void => {
    setOpen(false);
    onSwitchTaskDisplay?.(mode);
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-[color:var(--app-modal-overlay)]" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-24 z-50 w-full max-w-xl -translate-x-1/2 rounded-2xl border border-[color:var(--app-border)] bg-[color:var(--app-panel)] shadow-2xl"
        >
          <VisuallyHidden>
            <Dialog.Title>Command palette</Dialog.Title>
          </VisuallyHidden>
          <Command label="Command palette" data-testid="command-palette" className="flex flex-col">
            <Command.Input
              placeholder="Type a command…"
              className="w-full border-b border-[color:var(--app-border)] bg-transparent px-4 py-3 text-sm text-[color:var(--app-text)] placeholder:text-[color:var(--app-text-soft)] focus:outline-none"
            />
            <Command.List className="max-h-80 overflow-y-auto p-1">
              <Command.Empty className="px-4 py-3 text-sm text-[color:var(--app-text-muted)]">
                No matching commands.
              </Command.Empty>

              <Command.Group heading="Tasks" className="text-xs text-[color:var(--app-text-soft)]">
                <Command.Item
                  value="new-task"
                  onSelect={focusQuickAdd}
                  className="cursor-pointer rounded-xl px-3 py-2 text-sm text-[color:var(--app-text)] aria-selected:bg-[color:var(--app-accent-soft)]"
                >
                  New task
                </Command.Item>
                <Command.Item
                  value="switch-task-display-list"
                  onSelect={() => switchTaskDisplay('list')}
                  className="cursor-pointer rounded-xl px-3 py-2 text-sm text-[color:var(--app-text)] aria-selected:bg-[color:var(--app-accent-soft)]"
                >
                  Show task list
                </Command.Item>
                <Command.Item
                  value="switch-task-display-board"
                  onSelect={() => switchTaskDisplay('board')}
                  className="cursor-pointer rounded-xl px-3 py-2 text-sm text-[color:var(--app-text)] aria-selected:bg-[color:var(--app-accent-soft)]"
                >
                  Show task board
                </Command.Item>
                <Command.Item
                  value="switch-task-display-calendar"
                  onSelect={() => switchTaskDisplay('calendar')}
                  className="cursor-pointer rounded-xl px-3 py-2 text-sm text-[color:var(--app-text)] aria-selected:bg-[color:var(--app-accent-soft)]"
                >
                  Show task calendar
                </Command.Item>
                <Command.Item
                  value="switch-task-display-timeline"
                  onSelect={() => switchTaskDisplay('timeline')}
                  className="cursor-pointer rounded-xl px-3 py-2 text-sm text-[color:var(--app-text)] aria-selected:bg-[color:var(--app-accent-soft)]"
                >
                  Show task timeline
                </Command.Item>
              </Command.Group>

              <Command.Group
                heading="Projects"
                className="text-xs text-[color:var(--app-text-soft)]"
              >
                <Command.Item
                  value="new-project"
                  onSelect={newProject}
                  className="cursor-pointer rounded-xl px-3 py-2 text-sm text-[color:var(--app-text)] aria-selected:bg-[color:var(--app-accent-soft)]"
                >
                  New project…
                </Command.Item>
                {projects.map((project) => (
                  <Command.Item
                    key={project.id}
                    value={`switch-project-${project.name}`}
                    onSelect={() => switchProject(project.id)}
                    className="cursor-pointer rounded-xl px-3 py-2 text-sm text-[color:var(--app-text)] aria-selected:bg-[color:var(--app-accent-soft)]"
                  >
                    Switch to {project.name}
                  </Command.Item>
                ))}
              </Command.Group>

              <Command.Group heading="System" className="text-xs text-[color:var(--app-text-soft)]">
                <Command.Item
                  value="reset-local-data"
                  onSelect={resetLocal}
                  className="cursor-pointer rounded-xl px-3 py-2 text-sm text-[color:var(--app-text)] aria-selected:bg-[color:var(--app-accent-soft)]"
                >
                  Reset local data
                </Command.Item>
              </Command.Group>
            </Command.List>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
